import { cn, tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'

import { Slot } from './Slot'

export const stackVariants = tv({
  base: 'flex min-w-0',

  defaultVariants: {
    align: 'stretch',
    direction: 'vertical',
    gap: 'none',
    grow: 'auto',
    justify: 'start',
    width: 'auto',
    wrap: 'nowrap',
  },

  variants: {
    align: {
      center: 'items-center',
      end: 'items-end',
      start: 'items-start',
      stretch: 'items-stretch',
    },

    direction: {
      horizontal: 'flex-row',
      responsive: 'flex-col lg:flex-row',
      responsiveSm: 'flex-col sm:flex-row',
      vertical: 'flex-col',
    },

    gap: {
      lg: 'gap-6',
      md: 'gap-4',
      none: 'gap-0',
      sm: 'gap-2',
      snug: 'gap-1.5',
      xl: 'gap-8',
      xs: 'gap-1',
    },

    grow: {
      auto: null,
      fill: 'flex-1',
    },

    justify: {
      between: 'justify-between',
      center: 'justify-center',
      end: 'justify-end',
      start: 'justify-start',
    },

    overflow: {
      contain: 'min-h-0',
    },

    width: {
      auto: '',
      full: 'w-full',
    },

    wrap: {
      nowrap: 'flex-nowrap',
      wrap: 'flex-wrap',
    },
  },
})

export const stackItemVariants = tv({
  base: 'min-w-0',

  defaultVariants: {
    share: 'auto',
  },

  variants: {
    overflow: {
      contain: 'min-h-0',
    },

    share: {
      auto: null,
      fill: 'flex-1',
      fixed: 'shrink-0',
      major: 'w-full lg:flex-[7_1_0%]',
      minor: 'w-full lg:flex-[5_1_0%]',
      primary: 'w-full lg:flex-[3_1_0%]',
      secondary: 'w-full lg:flex-[1_1_0%]',
    },
  },
})

interface StackProps
  extends React.ComponentProps<'div'>, VariantProps<typeof stackVariants> {
  asChild?: boolean
}

function Stack({
  align,
  asChild,
  className,
  direction,
  gap,
  grow,
  justify,
  overflow,
  width,
  wrap,
  ...props
}: StackProps) {
  const Component = asChild ? Slot : 'div'
  return (
    <Component
      className={cn(
        stackVariants({
          align,
          direction,
          gap,
          grow,
          justify,
          overflow,
          width,
          wrap,
        }),
        className,
      )}
      {...props}
    />
  )
}

interface StackItemProps
  extends React.ComponentProps<'div'>, VariantProps<typeof stackItemVariants> {
  asChild?: boolean
}

function StackItem({
  asChild,
  className,
  overflow,
  share,
  ...props
}: StackItemProps) {
  const Component = asChild ? Slot : 'div'
  return (
    <Component
      className={cn(
        stackItemVariants({
          overflow,
          share,
        }),
        className,
      )}
      {...props}
    />
  )
}

export { Stack, StackItem }
export type { StackItemProps, StackProps }
